import { memo, useMemo } from 'react';
import { css } from '@emotion/react';
import { Chart } from '@frontend/charts';
import {
  DashboardWidget,
  TimePeriodListBoxMenu,
  useTimePeriodListBoxMenuState,
  type DashboardWidgetFC,
} from '@frontend/grid-dashboard';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { ContentLoader } from '@frontend/design-system';
import { useSchedulePulseInsuranceAppointmentStats } from '../hooks/schedule-pulse-hooks/use-schedule-pulse-insurance-appointment-stats';
import { aggregateVerificationCounts } from './helpers';
/**
 * @dashboard-widget
 *
 * id: insurance-analytics-widget
 * title: Insurance Analytics
 * description: Track insurance performance by verified, unverified, and error statuses.
 * icon: shield
 */

export const InsuranceAnalyticsWidget: DashboardWidgetFC = () => {
  const { selectedLocationIds } = useAppScopeStore();
  const { startDate, endDate, ...timePeriodMenuState } = useTimePeriodListBoxMenuState();

  const { isLoading, insuranceStatuses } = useSchedulePulseInsuranceAppointmentStats({
    widgetStartDate: startDate,
    widgetEndDate: endDate,
    locationIds: selectedLocationIds,
  });

  const results = useMemo(() => aggregateVerificationCounts(insuranceStatuses), [insuranceStatuses]);
  const data = useMemo(
    () => [
      { key: 'verified', label: 'Verified', value: results['Auto-Verified'] + results['Manual Verified'] },
      { key: 'error', label: 'Error', value: results['Error'] },
      { key: 'unverified', label: 'Unverified', value: results['Unverified'] },
    ],
    [results]
  );

  return (
    <DashboardWidget>
      <DashboardWidget.Header>
        <div css={{ flexGrow: 1, display: 'flex', flexDirection: 'row-reverse' }}>
          <TimePeriodListBoxMenu {...timePeriodMenuState} />
        </div>
      </DashboardWidget.Header>
      <DashboardWidget.Content css={{ display: 'flex', flex: 1 }}>
        {isLoading ? <ContentLoader show /> : <InsuranceAnalyticsPieChart data={data} centerValue={results['Total']} />}
      </DashboardWidget.Content>
    </DashboardWidget>
  );
};

InsuranceAnalyticsWidget.config = {
  size: 'medium-narrow',
  feature: 'insurance',
};

interface InsuranceAnalyticsPieChartDataInfo {
  key: string;
  label: string;
  value: number;
}
interface InsuranceAnalyticsPieChartProps {
  data: InsuranceAnalyticsPieChartDataInfo[];
  centerValue: number;
}

const InsuranceAnalyticsPieChart = memo<InsuranceAnalyticsPieChartProps>(
  ({ data, centerValue }: InsuranceAnalyticsPieChartProps) => {
    const { labels, groups, legendValues } = useMemo(() => {
      return data.reduce<{
        labels: Record<string, string>;
        groups: { name: string; value: number }[];
        legendValues: Record<string, number | string>;
        total: number;
      }>(
        (acc, item) => {
          acc.labels = {
            ...acc.labels,
            [item.key]: item.label,
          };
          acc.groups.push({
            name: item.key,
            value: item.value,
          });
          acc.legendValues[item.key] = item.value;
          return acc;
        },
        {
          labels: {},
          groups: [],
          legendValues: {},
          total: 0,
        }
      );
    }, [data]);

    return (
      <Chart
        colors={{
          verified: theme.colors.success50,
          error: theme.colors.critical50,
          unverified: theme.colors.warning20,
        }}
        labels={labels}
        style={{ marginBottom: 0 }}
        css={chartStyle}
      >
        <Chart.HorizontalContainer css={horizontalContainerStyle} alignItems='center'>
          <Chart.PieChart
            css={css`
              min-width: 50%;
              max-width: 50%;
            `}
            data={{
              groups: groups,
              responsiveCenterLabels: {
                textSpacing: -2,
                textVerticalOffset: 0,
                primaryText: {
                  label: centerValue,
                  textSizeRatio: 2.5,
                },
                secondaryText: {
                  label: 'Total',
                  textSizeRatio: 1.3,
                },
              },
            }}
            appearance={{
              height: '100%',
              innerRadius: '65%',
              outerRadius: '90%',
              borderRadius: 5,
              paddingAngle: 5,
              hideTooltip: true,
            }}
          />
          {centerValue && <Chart.Legends layout='vertical' values={legendValues} />}
        </Chart.HorizontalContainer>
      </Chart>
    );
  }
);

const horizontalContainerStyle = css({
  minWidth: '100%',
  margin: 0,
  gap: theme.spacing(2),
  flexWrap: 'nowrap',
  flexDirection: 'row',
});

const chartStyle = css({
  flexGrow: 1,
  '> div': {
    border: 'none',
    padding: 0,
    flexDirection: 'row',
    justifyContent: 'space-evenly',
    gap: 0,
  },
});
